import { FunctionComponent, useState } from "react";
import "./styles/Hospitals.css";
import { delay, motion } from "framer-motion";
import {
  FiPhone,
  FiAlertCircle,
  FiMapPin,
  FiCheckCircle,
  FiBook,
  FiClipboard,
  FiCheckSquare,
  FiUserCheck,
  FiScissors,
  FiHeart,
  FiActivity,
  FiUserPlus,
  FiHome,
  FiArrowLeft,
  FiArrowRight,
} from "react-icons/fi";
import { TbHeartPlus } from "react-icons/tb";
import HospitalProps from "../../assets/pictures/Icons/hospitalProps";

interface HospitalsProps {}

const Hospitals: FunctionComponent<HospitalsProps> = () => {
  const [step, setStep] = useState(1);

  const lineVariants = {
    initial: { width: 0 },
    animate: { width: "100%", transition: { duration: 0.5 } },
  };

  const contentVariants = {
    step1: {
      transform: "translateX(0)",
      duration: 0.5,
    },
    step2: {
      transform: "translateX(-20%)",
      transition: { duration: 0.5, delay: 0.25 },
    },
    step3: {
      transform: "translateX(-40%)",
      transition: { duration: 0.5, delay: 0.25 },
    },
    step4: {
      transform: "translateX(-60%)",
      transition: { duration: 0.5, delay: 0.25 },
    },
    step5: {
      transform: "translateX(-80%)",
      transition: { duration: 0.5, delay: 0.25 },
    },
  };

  const eachContentVariants = {
    initial: {
      opacity: 0,
      height: "21.875rem",
      transition: { duration: 0.5, delay: 0 },
    },
    animate: {
      opacity: 1,
      height: "auto",
      transition: { duration: 0.5, delay: 0 },
    },
  };

  const scrollFun = () => {
    window.scrollTo({
      top: document.getElementById("hospitals")?.offsetTop ?? 0,
      behavior: "smooth",
    });
  };

  const stepBack = () => {
    if (step > 1) {
      setStep(step - 1);
      scrollFun();
    }
  };

  const stepForward = () => {
    if (step < 5) {
      setStep(step + 1);
      scrollFun();
    }
  };

  return (
    <div className="hospitals-section" id="hospitals">
      <div className="title">
        <h2>Operacje w szpitalu</h2>
      </div>
      <div className="steps-wrapper">
        <div className="steps-header">
          <div className="steps-number-line">
            <div
              className={step >= 1 ? "step-circle active" : "step-circle"}
              onClick={() => setStep(1)}
            >
              1
            </div>
            <div className="grey-line">
              <motion.div
                className="steps-line"
                variants={lineVariants}
                initial="initial"
                animate={step >= 2 ? "animate" : "initial"}
              />
            </div>
          </div>
          <div className="steps-number-line">
            <div
              className={step >= 2 ? "step-circle active" : "step-circle"}
              onClick={() => setStep(2)}
            >
              2
            </div>
            <div className="grey-line">
              <motion.div
                className="steps-line"
                variants={lineVariants}
                initial="initial"
                animate={step >= 3 ? "animate" : "initial"}
              />
            </div>
          </div>
          <div className="steps-number-line">
            <div
              className={step >= 3 ? "step-circle active" : "step-circle"}
              onClick={() => setStep(3)}
            >
              3
            </div>
            <div className="grey-line">
              <motion.div
                className="steps-line"
                variants={lineVariants}
                initial="initial"
                animate={step >= 4 ? "animate" : "initial"}
              />
            </div>
          </div>
          <div className="steps-number-line">
            <div
              className={step >= 4 ? "step-circle active" : "step-circle"}
              onClick={() => setStep(4)}
            >
              4
            </div>
            <div className="grey-line">
              <motion.div
                className="steps-line"
                variants={lineVariants}
                initial="initial"
                animate={step >= 5 ? "animate" : "initial"}
              />
            </div>
          </div>
          <div className="steps-number-line">
            <div
              className={step >= 5 ? "step-circle active" : "step-circle"}
              onClick={() => setStep(5)}
            >
              5
            </div>
          </div>
        </div>
        <motion.div
          className="steps-content-wrapper"
          initial="initial"
          animate={`step${step}`}
          variants={contentVariants}
        >
          <motion.div
            variants={eachContentVariants}
            initial="initial"
            animate={step === 1 ? "animate" : "initial"}
            className="content"
          >
            <div className="button-wrapper-end">
              <button className="button-primary" onClick={stepForward}>
                Jak zapisać dziecko na zabieg
                <FiArrowRight />
              </button>
            </div>
            <Step1 />
          </motion.div>
          <motion.div
            variants={eachContentVariants}
            initial="initial"
            animate={step === 2 ? "animate" : "initial"}
            className="content"
          >
            <div className="button-wrapper">
              <button className="button-secondary" onClick={stepBack}>
                <FiArrowLeft />
                Skierowanie dziecka na zabieg
              </button>
              <button className="button-primary" onClick={stepForward}>
                Procedura przyjęcia dziecka do szpitala
                <FiArrowRight />
              </button>
            </div>
            <Step2 />
          </motion.div>
          <motion.div
            variants={eachContentVariants}
            initial="initial"
            animate={step === 3 ? "animate" : "initial"}
            className="content"
          >
            <div className="button-wrapper">
              <button className="button-secondary" onClick={stepBack}>
                <FiArrowLeft />
                Jak zapisać dziecko na zabieg
              </button>
              <button className="button-primary" onClick={stepForward}>
                Zabiegi operacyjne
                <FiArrowRight />
              </button>
            </div>
            <StepHospitalProcedure />
          </motion.div>
          <motion.div
            variants={eachContentVariants}
            initial="initial"
            animate={step === 4 ? "animate" : "initial"}
            className="content"
          >
            <div className="button-wrapper">
              <button className="button-secondary" onClick={stepBack}>
                <FiArrowLeft />
                Procedura przyjęcia dziecka do szpitala
              </button>
              <button className="button-primary" onClick={stepForward}>
                Procedura zwolnienia dziecka do domu
                <FiArrowRight />
              </button>
            </div>
            <StepSurgeryProcedure />
          </motion.div>
          <motion.div
            variants={eachContentVariants}
            initial="initial"
            animate={step === 5 ? "animate" : "initial"}
            className="content"
          >
            <div className="button-wrapper-start">
              <button className="button-secondary" onClick={stepBack}>
                <FiArrowLeft />
                Zabiegi operacyjne
              </button>
            </div>
            <StepReleaseToHome />
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

const Step1 = () => {
  return (
    <div className="step">
      <div className="step-header">
        <HospitalProps className="step-icon" />
        <h2>Skierowanie dziecka na zabieg</h2>
      </div>
      <p className="step-description">
        W celu wykonania niezbędnego zabiegu operacyjnego, którego nie można
        wykonać w warunkach ambulatoryjnych:
      </p>
      <ul className="step-list">
        <li>
          <FiCheckCircle className="list-icon" />
          Dziecko kierowane jest do szpitala na Oddział Chirurgii Dziecięcej,
          gdzie będzie przybywało pod opieką rodzica.
        </li>
        <li>
          <FiCheckCircle className="list-icon" />
          Rodzice mogą wybrać dowolny oddział w Polsce według własnego uznania.
        </li>
        <li>
          <FiCheckCircle className="list-icon" />
          Osobiście operuję i nadzoruję przebieg pooperacyjny moich pacjentów w
          szpitalu w Gostyniu.
        </li>
      </ul>
      <p className="step-note">
        Zabiegi wykonywane są w ramach kontraktu z NFZ.
      </p>
      <div className="alert-info">
        <FiMapPin className="alert-icon" />
        <p>plac Karola Marcinkowskiego 8, 63-800 Gostyń. Tel: 65 322 68 06</p>
        <button
          className="button-primary"
          onClick={() => {
            window.open(
              "https://www.google.com/maps/place/Oddzia%C5%82+Chirurgii+Dzieci%C4%99cej/@51.882721,17.010729,1374m/data=!3m2!1e3!4b1!4m6!3m5!1s0x47050c3b4f6756ef:0x39323bd2588b67e2!8m2!3d51.882721!4d17.010729!16s%2Fg%2F11hzwbhwy8?coh=192189&entry=tts&g_ep=EgoyMDI0MDkxMC4wKgBIAVAD",
              "_blank"
            );
          }}
        >
          Lokalizacja
        </button>
      </div>
    </div>
  );
};

const Step2 = () => {
  return (
    <div className="step">
      <div className="step-header">
        <FiPhone className="step-icon" />
        <h2>Jak zapisać dziecko na zabieg</h2>
      </div>
      <p className="step-description">
        Przy rejestracji telefonicznej (<strong>tel. 65 32 26 806</strong>, w
        dni powszednie w godz. <strong>11:00 - 13:00</strong>) trzeba podać:
      </p>
      <ul className="step-list">
        <li>
          <FiCheckCircle className="list-icon" />
          Podstawowe dane dziecka
        </li>
        <li>
          <FiCheckCircle className="list-icon" />
          Rozpoznanie choroby ze skierowania
        </li>
        <li>
          <FiCheckCircle className="list-icon" />
          Proponowany termin zabiegu ustalony z lekarzem kierującym
        </li>
      </ul>
      <p className="step-note">
        Zarezerwujemy dla Państwa najszybszy możliwy termin.
      </p>
      <div className="alert">
        <FiAlertCircle className="alert-icon" />
        <p>
          <strong>WAŻNE:</strong> Jeśli z powodu choroby dziecka lub innych
          wydarzeń losowych nie możecie Państwo skorzystać z rezerwacji, należy
          ją <strong>niezwłocznie odwołać</strong>, aby zwolnić miejsce dla
          innych. Jednocześnie można ustalić nowy termin.
        </p>
      </div>
    </div>
  );
};

const StepHospitalProcedure = () => {
  return (
    <div className="step">
      <div className="step-header">
        <FiClipboard className="step-icon" />
        <h2>Procedura przyjęcia dziecka do szpitala</h2>
      </div>
      <p className="step-description">
        Należy zgłosić się we wcześniej ustalonym terminie od godz.{" "}
        <strong>12.30 w poniedziałek</strong> do Izby Przyjęć na parterze.
        Dziecko do zabiegu powinno być całkiem zdrowe:
      </p>
      <ul className="step-list">
        <li>
          <FiCheckSquare className="list-icon" />W ciągu 14 dni przed zabiegiem
          nie może pobierać antybiotyków, mieć wykonywanych szczepień
          ochronnych.
        </li>
        <li>
          <FiCheckSquare className="list-icon" />
          Dziewczęta nie powinny być w trakcie miesiączki. W przeciwnym razie
          termin zabiegu należy przełożyć.
        </li>
      </ul>
      <p className="step-subheader">Należy posiadać przy sobie:</p>
      <ul className="step-list">
        <li>
          <FiBook className="list-icon" />
          Książeczkę zdrowia dziecka z wpisami szczepień i numerem PESEL.
        </li>
        <li>
          <FiBook className="list-icon" />
          Kartę informacyjną poprzedniego pobytu w szpitalu (jeśli było
          hospitalizowane).
        </li>
        <li>
          <FiUserCheck className="list-icon" />
          Zaświadczenie od specjalisty (np. kardiologa, neurologa) o braku
          przeciwskazań.
        </li>
        <li>
          <FiClipboard className="list-icon" />
          Zdjęcia RTG, płyty z badaniami RTG/TK/NMR, jeśli były wykonywane.
        </li>
      </ul>
      <p className="step-description">
        Pielęgniarka zbierze dane osobowe i założy historię choroby. Na II
        piętrze lekarz zweryfikuje dane medyczne, sprawdzi zgodność rozpoznania
        choroby ze stanem faktycznym i wyjaśni ew. wątpliwości.
      </p>
      <p className="step-subheader">Dalsze kroki:</p>
      <ul className="step-list">
        <li>
          <FiCheckSquare className="list-icon" />
          Dziecku zostanie założony identyfikator na rękę.
        </li>
        <li>
          <FiCheckSquare className="list-icon" />
          Pielęgniarka pobierze krew do badania.
        </li>
        <li>
          <FiCheckSquare className="list-icon" />
          Lekarz anestezjolog zbada dziecko pod kątem przeciwwskazań do
          znieczulenia.
        </li>
        <li>
          <FiClipboard className="list-icon" />
          Rodzice wypełnią ankietę anestezjologiczną dotyczącą stanu zdrowia
          dziecka.
        </li>
        <li>
          <FiClipboard className="list-icon" />
          Należy zgłosić w sekretariacie chęć otrzymania "opieki nad dzieckiem"
          na czas pobytu w szpitalu i opieki pooperacyjnej
        </li>
      </ul>
    </div>
  );
};

const StepSurgeryProcedure = () => {
  return (
    <div className="step">
      <div className="step-header">
        <FiScissors className="step-icon" />
        <h2>Zabiegi operacyjne</h2>
      </div>
      <p className="step-description">
        Zabiegi przeprowadzamy na nowoczesnym Bloku Operacyjnym, wyposażonym w
        sprzęt niezbędny do bezpiecznego przeprowadzenia znieczulenia ogólnego.
        Proces operacyjny wygląda następująco:
      </p>
      <ul className="step-list">
        <li>
          <FiActivity className="list-icon" />
          Dziecko jest przewożone z Oddziału na Blok Operacyjny po podaniu tzw.
          premedykacji, leków zmniejszających stres i ułatwiających wprowadzenie
          do znieczulenia.
        </li>
        <li>
          <FiHeart className="list-icon" />
          Po operacji dziecko przebywa na sali wybudzeniowej pod nadzorem
          lekarzy i pielęgniarki anestezjologicznej.
        </li>
        <li>
          <FiUserPlus className="list-icon" />
          Dziecko zostaje przekazane na Oddział Chirurgii, gdzie odpoczywa po
          zabiegu. Ważne jest, by nie było niepotrzebnie niepokojone.
        </li>
        <li>
          <TbHeartPlus className="list-icon" />
          Obecność Rodzica przy łóżku małego Pacjenta to poczucie bezpieczeństwa
          i komfortu.
        </li>
      </ul>
    </div>
  );
};

const StepReleaseToHome = () => {
  return (
    <div className="step">
      <div className="step-header">
        <FiHome className="step-icon" />
        <h2>Procedura zwolnienia dziecka do domu</h2>
      </div>
      <p className="step-description">
        Po spędzeniu jednej nocy po zabiegu, dzieci, które nie mają żadnych
        zastrzeżeń pooperacyjnych, mogą być zwolnione do domu. W niektórych
        przypadkach dziecko będzie musiało pozostać w szpitalu na dłużej, o czym
        rodzice zostaną poinformowani przez chirurga.
      </p>
      <ul className="step-list">
        <li>
          <FiCheckCircle className="list-icon" />
          Rano odbywa się wizyta lekarska, podczas której lekarze decydują o
          wypisie dziecka do domu.
        </li>
        <li>
          <FiClipboard className="list-icon" />
          Podczas wizyty należy zgłosić chęć otrzymania "opieki nad dzieckiem"
          na czas pobytu w szpitalu.
        </li>
        <li>
          <FiHome className="list-icon" />
          Po wizycie rodzice odbierają kartę informacyjną w sekretariacie.
        </li>
      </ul>
    </div>
  );
};

export default Hospitals;
